import React from 'react';
import styled, { css } from 'styled-components';
import { scrollToElement } from '@99designs/browser';
import { Box, Button } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { boxShadows, breakpoints, colors, maxWidths } from '@99designs/emu/settings';
import { IconLeft4 } from '@99designs/icons';
import { trackEvent } from '@99designs/tracking';
import { useOnboarder } from '../OnboarderContext';
import { useOnboarderNav } from '../useOnboarderNav';
import { ProgressDisplay } from './ProgressDisplay';

export const ControlsClearanceHeight = '100px';

const StyledBox = styled(Box)<{ canGoToNextStep: boolean }>`
    box-shadow: ${boxShadows.high};
    width: 100%;
    position: fixed;
    bottom: 0;
    background-color: ${colors.white};
    ${mediaQuery.only('small')} {
        position: ${({ canGoToNextStep }) => (canGoToNextStep ? 'fixed' : 'relative')};
    }
`;

const resetButtonStyles = css`
    border: none;
    border-radius: 0;
    background: transparent;
    padding: 0;
    &:hover,
    &:focus {
        box-shadow: none;
        text-decoration: none;
    }
`;

const accessibiltyMinWidth = 44;

const BackButton = styled.button`
    ${resetButtonStyles}
    min-width: ${accessibiltyMinWidth}px;
    margin-right: -9px;
    font-size: 1.8em;
    color: ${colors.grey800};
    &:hover {
        cursor: pointer;
    }
    &:focus {
    }
`;

const ContinueButton = styled(Button)`
    margin-left: auto;
` as typeof Button;

const ClearControlsSpacer = styled(Box)<{ canGoToNextStep: boolean }>`
    ${mediaQuery.only('small')} {
        display: ${({ canGoToNextStep }) => (canGoToNextStep ? 'block' : 'none')};
    }
`;
interface OnboarderControlsProps {
    headerRef: React.RefObject<HTMLDivElement>;
}
export function OnboarderControls({ headerRef }: OnboarderControlsProps) {
    const onboarderNav = useOnboarderNav();
    const { data } = useOnboarder();
    const canGoToNextStep = onboarderNav.currentStep().canGoToNextStep(data);

    return (
        <>
            <ClearControlsSpacer
                height={ControlsClearanceHeight}
                canGoToNextStep={canGoToNextStep}
            />
            <StyledBox canGoToNextStep={canGoToNextStep} role="navigation">
                <Box
                    alignSelf="center"
                    display="flex"
                    maxWidth={`${maxWidths.content.widescreen}px`}
                    padding="1.5bu"
                    alignItems="center"
                    margin="auto"
                >
                    <BackButton
                        name={__('Go back')}
                        aria-label={__('Go back')}
                        onClick={() => {
                            trackEvent('Clicked Back Button');
                            onboarderNav.goToPreviousStep();

                            if (window.innerWidth < breakpoints['normal'][0]) {
                                scrollToElement(headerRef.current, 30);
                            }
                        }}
                        data-testid="onboarder-back-button"
                    >
                        <IconLeft4 />
                    </BackButton>
                    <ProgressDisplay
                        currentStepIndex={onboarderNav.currentStepIndex()}
                        totalSteps={onboarderNav.totalSteps()}
                    />
                    <ContinueButton
                        disabled={!canGoToNextStep}
                        appearance="primary"
                        size="large"
                        data-testid="onboarder-continue-button"
                        onClick={() => {
                            onboarderNav
                                .currentStep()
                                .trackCompleted(data, onboarderNav.currentStepIndex() + 1);

                            if (window.innerWidth < breakpoints['normal'][0]) {
                                scrollToElement(headerRef.current, 30);
                            }
                            if (onboarderNav.isLastStep()) {
                                // goto recommendation
                                onboarderNav.goToRecommendation(data);
                                return;
                            }
                            onboarderNav.goToNextStep();
                        }}
                    >
                        {__('Continue')}
                    </ContinueButton>
                </Box>
            </StyledBox>
        </>
    );
}
